import React, { useState, useEffect } from 'react';
import { fetchTrains, fetchTrainDetail, fetchTrainStatus, fetchCongestion } from '../actions';
import moment from 'moment-timezone';
import { useParams } from 'react-router-dom';
import { findTrain, trainHtmlClass, destinationStationName, createTrainSchedule, displayDelay, displayOrchestrationNumber } from '../services/trainsService';
import { averageCrowdLevel } from '../services/congestionService';
import { eventsEmit } from '../services/appboxService';
import { trainKindClass } from '../data/trainKinds';
import { CROWD_UNAVAILABLE_MESSAGE } from '../data/crowdLevels';
import TrainSchedule from './trainDetail/TrainSchedule';
import TrainConsist from './trainDetail/TrainConsist.js';
import icTrainTx1000   from '../assets/images/mainline/train/detail/ic_train_tx1000.svg';
import icTrainTx2000   from '../assets/images/mainline/train/detail/ic_train_tx2000.svg';
import icTrainTx2000Zoubi   from '../assets/images/mainline/train/detail/ic_train_tx2000_zoubi.svg';
import icTrainTx3000   from '../assets/images/mainline/train/detail/ic_train_tx3000.svg';
import carLocal        from '../assets/images/mainline/train/detail/local.svg';
import carRapid        from '../assets/images/mainline/train/detail/rapid.svg';
import carSemiR        from '../assets/images/mainline/train/detail/semi_r.svg';
import carComR        from '../assets/images/mainline/train/detail/com_r.svg';
import icCloseDialog   from '../assets/images/trains/kinds/ic_close_dialog.svg';

const SEGMENTS = ['停車駅', '混雑度・車両情報'];

const TrainDetail = () => {
  const [train, setTrain] = useState('');
  const [trainSchedule, setTrainSchedule] = useState('');
  const [displayable, setdisplayable] = useState(false);
  const [trainStatus, setTrainStatus] = useState(0);
  const [congestionCars, setCongestionCars] = useState([]);
  const [congestionLoaded, setCongestionLoaded] = useState(false);
  const [activeSegment, setActiveSegment] = useState(0);
  const { train_number } = useParams();

  useEffect(() => {
    import('..//assets/stylesheets/mainline/trains/detail.css')
      .then(() => {
      })
      .catch(err => console.error("CSSの読み込みエラー", err));

    async function fetchTrainDetailJson() {
      try {
        const trainsJson = await fetchTrains();
        const trainData = findTrain(trainsJson.trains, train_number)
        setTrain(trainData);

        const m = moment.tz(trainsJson.updated_at * 1000, 'Asia/Tokyo');
        // 0〜4時の場合は前日扱い
        if (m.hour() >= 0 && m.hour() < 4) {
          m.subtract(1, 'day');
        }
        const trainUpdatedAt = m.format("YYYY-MM-DD");
        const trainDetailJson = await fetchTrainDetail(trainUpdatedAt, train_number);

        const trainStatusJson = await fetchTrainStatus();
        const trainStatus = trainStatusJson.category
        // 0 以外は異常
        if (trainStatusJson.category !== 0) {
          setTrainStatus(trainStatusJson.category);
        }

        try {
          const congestionJson = await fetchCongestion(train_number);
          setCongestionCars(congestionJson.train);
        } catch (error) {
          console.error('混雑度情報の取得に失敗:', error);
        } finally {
          setCongestionLoaded(true);
        }

        setTrainSchedule(createTrainSchedule(trainData, trainDetailJson));
        setdisplayable(true);
        eventsEmit('train_detail.screen', { train_number: train_number });
      } catch (error) {
        setdisplayable(true);
        console.error('列車情報の取得に失敗:', error);
      }
    }

    fetchTrainDetailJson();
  }, []);

  const trainIconSvgs = {
    'tx1000': icTrainTx1000,
    'tx2000': icTrainTx2000,
    'tx2000-zoubi': icTrainTx2000Zoubi,
    'tx3000': icTrainTx3000,
  };

  const trainIconSvg = trainIconSvgs[trainHtmlClass(train)] || null;
  const trainIcon = train && trainIconSvg
    ? <img className="train-detail-icon" src={trainIconSvg} alt={`Train ${trainHtmlClass(train)}`} />
    : <div className="train-detail-icon" />;

  const trainKindSvgs = {
    'local': carLocal,
    'com_r': carComR,
    'rapid': carRapid,
    'semi_r': carSemiR,
  };

  const trainKindSvg = () => {
    return trainKindSvgs[trainKindClass(train.train_kind_id)] || null;
  };

  const crowdLevel = train ? averageCrowdLevel(congestionCars) : null;
  // 運転見合わせ、または遅延が60分以上の場合は時刻表示を抑制する
  const isSuspended = trainStatus === 2 || train.delay >= 3600;

  const clickSegment = (index) => {
    setActiveSegment(index);
    if (index === 0) {
      eventsEmit(`train_detail.switching.${train_number}.stop`, {});
    } else if (index === 1) {
      eventsEmit(`train_detail.switching.${train_number}.congestion_level_car_infomation`, {});
      eventsEmit(`congestion_situation.screen.${train_number}`, {});
    }
  };

  const clickCloseButton = (event) => {
    event.preventDefault();
    if (activeSegment === 1) {
      eventsEmit('congestion_situation.close', {});
    } else {
      eventsEmit('train_detail.close', {});
    }
    window.location.href = event.currentTarget.href;
  };

  return (
    <div>
      <main className="main-container">
        <div className="half-modal">
          <div className="half-modal-window">
            <div className="modal-header">
              <div className="segment-control">
                {SEGMENTS.map((label, i) => (
                  <button
                    key={label}
                    className={`segment-control-item${activeSegment === i ? ' is-active' : ''}`}
                    onClick={() => clickSegment(i)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="modal-close">
                <a
                  href={`/lines/mainline/trains#train${train_number}`}
                  className="close-button"
                  onClick={clickCloseButton}
                >
                  <img
                    src={icCloseDialog}
                    alt="Close"
                  />
                </a>
              </div>
            </div>
            <div className="train-detail">
              <div className="train-detail-block">
                {trainIcon}
              </div>
              <div className="train-detail-block">
                <div className="train-detail-row">
                  <img
                    src={trainKindSvg()}
                    className="train-detail-type"
                    alt="Car icon"
                  />
                  <p className="train-detail-destination">{destinationStationName(train)}<span>行き</span></p>
                  <span className="train-detail-consist-count">{train.train_length_id}両</span>
                </div>
                <div className="train-detail-row">
                  {crowdLevel ? (
                    <React.Fragment>
                      <img src={crowdLevel.icon} className="train-detail-crowd-level-icon" alt={crowdLevel.label} />
                      <span className="train-detail-crowd-level-text">{crowdLevel.label}</span>
                    </React.Fragment>
                  ) : congestionLoaded ? (
                    <span className="train-detail-crowd-level-text">{CROWD_UNAVAILABLE_MESSAGE}</span>
                  ) : null}
                </div>
                <div className="train-detail-row">
                  <p className="train-detail-number">列車番号 {train.train_number}</p>
                  <p className="train-detail-consist-number">編成番号 TX-{displayOrchestrationNumber(train.train_orchestration_number)}</p>
                </div>
              </div>
            </div>
            {train.delay > 59 && (
              <div className="train-delay-time">遅れ約{displayDelay(train.delay)}分</div>
            )}
            {isSuspended && (
              <p className="train-delay-text">現在、時刻を非表示にしております</p>
            )}
            <div className={`segment-panel${activeSegment === 0 ? ' is-active' : ''}`}>
              <div className="train-schedule">
                <div className="train-schedule-header">
                  <span className="train-schedule-header-item">停車駅</span>
                  <span className="train-schedule-header-item">到着時刻 定刻</span>
                  <span className="train-schedule-header-item">見込み</span>
                </div>
                <div className="train-schedule-body">
                  {!displayable ? null : (
                    trainSchedule.length > 0 ? (
                      <TrainSchedule
                        schedules={trainSchedule}
                        train={train}
                        isSuspended={isSuspended}
                      />
                    ) : (
                      <div className="notice">この列車の停車駅・発車時刻は<br/>表示できません。</div>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className={`segment-panel${activeSegment === 1 ? ' is-active' : ''}`}>
              {train ? (<TrainConsist train={train} congestions={congestionCars}/>) : null}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrainDetail;
